import React, { useEffect, useRef, useState } from "react";
import { GridCellState } from "../GridCellState";
import { ClueSpec } from "../ClueSpec";
import { Mode } from "./CrosswordMode";
import { Clue } from "../models/clue";
import { clueDividers } from "../utils/GridUtils";
import "./CrosswordCell.css";
import { generateDistinctColorOptimized } from "../utils/colors";

interface CrosswordCellProps {
  cell: GridCellState;
  rowIndex: number;
  colIndex: number;
  selectedCell: { row: number; col: number } | null;
  selectedClue: ClueSpec;
  mode: Mode;
  clues: Clue[];
  onCellClick: (
    rowIndex: number,
    colIndex: number,
    cell: GridCellState,
    automove: boolean
  ) => void;
  isPuzzleComplete: boolean;
  isUpdating: boolean;
  numberOfUsers: number;
}
export const fadeTime = 1000;
const CrosswordCell: React.FC<CrosswordCellProps> = ({
  cell,
  rowIndex,
  colIndex,
  selectedCell,
  selectedClue,
  mode,
  clues,
  onCellClick,
  isPuzzleComplete,
  isUpdating,
  numberOfUsers
}) => {
  const handleClick = () => {
    onCellClick(rowIndex, colIndex, cell, false);
  };
  const [backgroundColor, setBackgroundColor] = useState<string>(cell.isBlack() ? "black" : "transparent");
  const fadeTimeout = useRef<NodeJS.Timeout | null>(null);

  // Immediate visual feedback for cell updates, then fade - intentional animation trigger
  useEffect(() => {
    if (isUpdating) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setBackgroundColor('gold');
      if (fadeTimeout.current) {
        clearTimeout(fadeTimeout.current);
      }
      fadeTimeout.current = setTimeout(() => {
        setBackgroundColor(cell.isBlack() ? "black" : "transparent");
      }, fadeTime); // Fade over 1 second
    } else {
      setBackgroundColor(cell.isBlack() ? "black" : "transparent");
      if (fadeTimeout.current) {
        clearTimeout(fadeTimeout.current);
      }
    }

    return () => {
      if (fadeTimeout.current) {
        clearTimeout(fadeTimeout.current);
      }
    };
  }, [isUpdating,cell]);
  
  return (
    <div
      key={`${rowIndex}-${colIndex}`}
      className={`crossword-cell ${cell.isBlack() ? "black" : ""} ${
        selectedCell &&
        selectedCell.row === rowIndex &&
        selectedCell.col === colIndex
          ? "selected-cell"
          : ""
      } ${
        selectedClue.containsCell(rowIndex, colIndex, clues) && !isPuzzleComplete
          ? "selected-clue"
          : ""
      } ${clueDividers(
        cell.containingAcrossClue,
        cell.containingDownClue,
        rowIndex,
        colIndex
      )}`}
      onClick={handleClick}
      style={{
        width: "30px",
        height: "30px",
        boxSizing: "border-box",
        backgroundColor: (isPuzzleComplete && cell.enteringUserKey>="A") ? generateDistinctColorOptimized(cell.enteringUserKey,numberOfUsers,true) : backgroundColor 
    }}
    >
      {cell.showText && (
        <>
          {cell.clueNum.isSet() && (
            <sup
              className={
            cell.getText(mode) === ""
              ? "cluenum no-text"
              : "cluenum with-text"
              }
            >
              {cell.clueNum.number}
            </sup>
          )}
          <span className={`${cell.getClass(mode)}`} >{cell.getText(mode)}</span>
        </>
      )}
    </div>
  );
};

export default CrosswordCell;
